/**
 * ChatInput - Message input with @ tagging support and file attachments
 *
 * Implements:
 * - HOME-03: Composer supports @ tagging for one or multiple agents
 * - HOME-10: File attachment support
 * - Keyboard shortcuts (Enter to send, Shift+Enter for newline)
 *
 * Features:
 * - Auto-resizing textarea
 * - @ mention autocomplete popup
 * - Pending file previews
 * - Attach button with drag-drop support
 */

import {
  useState,
  useRef,
  useCallback,
  useEffect,
  type KeyboardEvent,
  type ChangeEvent,
} from 'react';
import { FileUpload } from './FileUpload';
import { usePermissions } from '../../hooks/usePermissions';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Agent definition for @ tagging.
 * In the future this could come from API/context.
 */
interface Agent {
  id: string;
  name: string;
}

/**
 * Pending file waiting to be sent with message.
 */
interface PendingFile {
  /** The file object */
  file: File;
  /** Preview URL for images (created via URL.createObjectURL) */
  preview?: string;
  /** Whether currently uploading */
  uploading: boolean;
  /** Upload progress (0-100) */
  progress: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Available agents for @ tagging.
 * These are the built-in agent types.
 */
const AVAILABLE_AGENTS: Agent[] = [
  { id: 'researcher', name: 'Researcher' },
  { id: 'planner', name: 'Planner' },
  { id: 'analyst', name: 'Analyst' },
  { id: 'writer', name: 'Writer' },
];

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface ChatInputProps {
  /** Callback when user sends a message */
  onSend: (message: string, taggedAgents: string[], attachments: File[]) => void;
  /** Whether input is disabled (e.g., while streaming) */
  disabled?: boolean;
  /** Placeholder text for input */
  placeholder?: string;
  /** Whether user can upload files (RBAC gating - HOME-12) */
  canUpload?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ChatInput({
  onSend,
  disabled,
  placeholder,
  canUpload = true,
}: ChatInputProps) {
  // Check user permissions (RBAC - HOME-12)
  const permissions = usePermissions();

  // Input value state
  const [value, setValue] = useState('');

  // @ mention popup state
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);

  // File attachment state
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [showUpload, setShowUpload] = useState(false);

  // Textarea ref for auto-resize and focus
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // -------------------------------------------------------------------------
  // COMPUTED VALUES
  // -------------------------------------------------------------------------

  /**
   * Filter agents based on current @ mention search.
   */
  const filteredAgents = AVAILABLE_AGENTS.filter((agent) =>
    agent.name.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  // -------------------------------------------------------------------------
  // EFFECTS
  // -------------------------------------------------------------------------

  /**
   * Auto-resize textarea based on content.
   * Max height is 200px to prevent taking too much space.
   */
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [value]);

  // -------------------------------------------------------------------------
  // HANDLERS
  // -------------------------------------------------------------------------

  /**
   * Handle input change - detect @ mentions.
   */
  const handleChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setValue(newValue);

    // Check for @ mention trigger
    const lastAtIndex = newValue.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const textAfterAt = newValue.slice(lastAtIndex + 1);
      const hasSpaceAfter = textAfterAt.includes(' ');

      // Show mention popup if we're typing after @
      if (!hasSpaceAfter) {
        setMentionSearch(textAfterAt);
        setShowMentions(true);
        setMentionIndex(0);
        return;
      }
    }

    setShowMentions(false);
  }, []);

  /**
   * Handle mention selection from popup.
   */
  const selectMention = useCallback(
    (agent: Agent) => {
      const lastAtIndex = value.lastIndexOf('@');
      const newValue = value.slice(0, lastAtIndex) + `@${agent.name} `;
      setValue(newValue);
      setShowMentions(false);
      textareaRef.current?.focus();
    },
    [value]
  );

  /**
   * Handle file upload from drop zone or file picker.
   */
  const handleFileUpload = useCallback((files: File[]) => {
    const newPendingFiles = files.map((file) => ({
      file,
      // Create preview URL for images
      preview: file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : undefined,
      uploading: false,
      progress: 0,
    }));
    setPendingFiles((prev) => [...prev, ...newPendingFiles]);
    setShowUpload(false);
  }, []);

  /**
   * Remove a pending file from the list.
   */
  const removePendingFile = useCallback((index: number) => {
    setPendingFiles((prev) => {
      const newFiles = [...prev];
      // Revoke object URL to prevent memory leak
      if (newFiles[index].preview) {
        URL.revokeObjectURL(newFiles[index].preview!);
      }
      newFiles.splice(index, 1);
      return newFiles;
    });
  }, []);

  /**
   * Handle keyboard events for navigation and send.
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // Handle mention popup navigation
      if (showMentions) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setMentionIndex((i) => Math.min(i + 1, filteredAgents.length - 1));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setMentionIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter' && filteredAgents[mentionIndex]) {
          e.preventDefault();
          selectMention(filteredAgents[mentionIndex]);
        } else if (e.key === 'Escape') {
          setShowMentions(false);
        }
        return;
      }

      // Send on Enter (without Shift for newline)
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [showMentions, filteredAgents, mentionIndex, selectMention]
  );

  /**
   * Handle send button click or Enter key.
   */
  const handleSend = useCallback(() => {
    const trimmed = value.trim();

    // Don't send empty messages (unless there are files)
    if ((!trimmed && pendingFiles.length === 0) || disabled) return;

    // Extract tagged agents from message content
    const taggedAgents = AVAILABLE_AGENTS.filter((agent) =>
      trimmed.includes(`@${agent.name}`)
    ).map((agent) => agent.id);

    // Call onSend with message, agents, and files
    onSend(
      trimmed,
      taggedAgents,
      pendingFiles.map((pf) => pf.file)
    );

    // Clear input state
    setValue('');
    setPendingFiles([]);
  }, [value, pendingFiles, disabled, onSend]);

  // If user doesn't have chat access, show restricted message
  if (!permissions.canAccessChat) {
    return (
      <div className="chat-input-container">
        <div className="chat-restricted">
          <p>Chat access is restricted for your role.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-input-container">
      {/* @ Mention popup */}
      {showMentions && filteredAgents.length > 0 && (
        <div className="mention-popup">
          {filteredAgents.map((agent, index) => (
            <button
              key={agent.id}
              type="button"
              className={`mention-option ${index === mentionIndex ? 'selected' : ''}`}
              onClick={() => selectMention(agent)}
            >
              <span className="mention-icon">@</span>
              {agent.name}
            </button>
          ))}
        </div>
      )}

      {/* Pending files preview */}
      {pendingFiles.length > 0 && (
        <div className="pending-files">
          {pendingFiles.map((pf, index) => (
            <div key={index} className="pending-file">
              {pf.preview ? (
                <img src={pf.preview} alt={pf.file.name} />
              ) : (
                <span className="file-name">{pf.file.name}</span>
              )}
              <button
                type="button"
                className="remove-file"
                onClick={() => removePendingFile(index)}
              >
                x
              </button>
            </div>
          ))}
        </div>
      )}

      {/* File upload drop zone (expandable) */}
      {showUpload && canUpload && permissions.canUploadFiles && (
        <FileUpload
          onUpload={handleFileUpload}
          disabled={disabled}
          className="chat-file-upload"
        />
      )}

      {/* Input area with attach and send buttons */}
      <div className="chat-input">
        {/* Attach button (HOME-10, gated by HOME-12) */}
        {canUpload && permissions.canUploadFiles && (
          <button
            type="button"
            className="attach-button"
            onClick={() => setShowUpload(!showUpload)}
            disabled={disabled}
            title="Attach files"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
        )}

        {/* Message input */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || 'Type a message... (@ to tag agents)'}
          disabled={disabled}
          rows={1}
        />

        {/* Send button */}
        <button
          type="button"
          className="send-button"
          onClick={handleSend}
          disabled={disabled || (!value.trim() && pendingFiles.length === 0)}
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </button>
      </div>

      {/* Inline styles for chat input component */}
      <style>{`
        .chat-input-container {
          position: relative;
          padding: 12px 16px;
          border-top: 1px solid var(--color-border);
          background: var(--color-surface);
        }

        .chat-restricted {
          padding: 16px;
          text-align: center;
          color: var(--color-text-secondary);
          background: var(--color-surface-muted);
          border-radius: 8px;
        }

        .chat-restricted p {
          margin: 0;
          font-size: 14px;
        }

        .mention-popup {
          position: absolute;
          bottom: 100%;
          left: 16px;
          right: 16px;
          margin-bottom: 4px;
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: 8px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          max-height: 200px;
          overflow-y: auto;
        }

        .mention-option {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 10px 12px;
          border: none;
          background: transparent;
          text-align: left;
          font-size: 14px;
          color: var(--color-text);
          cursor: pointer;
        }

        .mention-option:hover, .mention-option.selected {
          background: var(--color-surface-muted);
        }

        .mention-icon {
          color: var(--color-accent);
          font-weight: 600;
        }

        .pending-files {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 8px;
        }

        .pending-file {
          position: relative;
          max-width: 80px;
        }

        .pending-file img {
          width: 60px;
          height: 60px;
          object-fit: cover;
          border-radius: 4px;
        }

        .pending-file .file-name {
          display: block;
          width: 60px;
          padding: 8px;
          background: var(--color-surface-muted);
          border-radius: 4px;
          font-size: 11px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .remove-file {
          position: absolute;
          top: -6px;
          right: -6px;
          width: 18px;
          height: 18px;
          border: none;
          border-radius: 50%;
          background: var(--color-error);
          color: white;
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .chat-file-upload {
          margin-bottom: 8px;
        }

        .chat-input {
          display: flex;
          gap: 8px;
          align-items: flex-end;
        }

        .attach-button {
          width: 36px;
          height: 36px;
          border: none;
          border-radius: 6px;
          background: transparent;
          color: var(--color-text-secondary);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .attach-button:hover:not(:disabled) {
          background: var(--color-surface-muted);
          color: var(--color-accent);
        }

        .attach-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .attach-button svg {
          width: 20px;
          height: 20px;
        }

        .chat-input textarea {
          flex: 1;
          padding: 10px 12px;
          border: 1px solid var(--color-border);
          border-radius: 8px;
          font-size: 14px;
          font-family: inherit;
          line-height: 1.5;
          resize: none;
          min-height: 42px;
          max-height: 200px;
        }

        .chat-input textarea:focus {
          outline: none;
          border-color: var(--color-focus);
          box-shadow: 0 0 0 3px var(--color-focus-ring);
        }

        .chat-input textarea::placeholder {
          color: var(--color-text-muted);
        }

        .chat-input textarea:disabled {
          background: var(--color-surface-muted);
          cursor: not-allowed;
        }

        .send-button {
          width: 42px;
          height: 42px;
          border: none;
          border-radius: 8px;
          background: var(--color-accent);
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.15s;
        }

        .send-button:hover:not(:disabled) {
          background: var(--color-accent-hover);
        }

        .send-button:disabled {
          background: var(--color-border);
          color: var(--color-text-muted);
          cursor: not-allowed;
        }

        .send-button svg {
          width: 20px;
          height: 20px;
        }
      `}</style>
    </div>
  );
}
